import MsgReader from '@kenjiuno/msgreader';
import iconv from 'iconv-lite';
import EmlParser from './eml-parser';
import htmlMessage from './message.html';
import attachmentMessage from './message-attachment.html';

export default class MailViewerController {
	#$compile;

	#$element;

	#$rootScope;

	#$timeout;

	#$translate;

	#Notification;

	/* @ngInject */
	/**
	 * @param {import('angular').ICompileService} $compile
	 * @param {import('angular').IRootElementService} $element
	 * @param {any} $rootScope
	 * @param {import('angular').ISCEService} $sce
	 * @param {import('angular').ITimeoutService} $timeout
	 * @param {import('angular').translate.ITranslateService} $translate
	 * @param {any} Notification
	 */
	constructor(
		$compile,
		$element,
		$rootScope,
		$sce,
		$timeout,
		$translate,
		Notification,
	) {
		this.#$compile = $compile;
		this.#$element = $element;
		this.#$rootScope = $rootScope;
		this.$sce = $sce;
		this.#$timeout = $timeout;
		this.#$translate = $translate;
		this.#Notification = Notification;

		/** @type {ArrayBuffer | DataView | undefined} */
		this.data = undefined;

		/** @type {any} */
		this.mail = undefined;

		/** @type {any} */
		this.type = undefined;

		/** @type {?MsgReader} */
		this.msgReaderInstance = null;
	}

	/**
	 * @param {string} headers
	 * @returns {any}
	 */
	fetchRawHeaders(headers) {
		if (!headers) {
			return {};
		}

		const aHeaders = headers.split(/\r?\n\t?/);

		/** @type {any} */
		const oHeaders = {};

		let kh;
		let vh;
		for (const header of aHeaders) {
			if (header.includes(': ')) {
				[kh, vh] = header.split(': ');
				oHeaders[kh] = vh;
			}
			else if (kh && !header.includes(': ')) {
				oHeaders[kh] += header;
			}
		}
		return oHeaders;
	}

	hasVisibleAttachments() {
		return Array.isArray(this.mail?.attachments)
			&& this.mail.attachments.some((/** @type {any} */ attachment) => attachment.visible);
	}

	/**
	 * @param {any} a
	 * @returns {boolean}
	 */
	isArray(a) {
		return Array.isArray(a);
	}

	/**
	 * @returns {boolean}
	 */
	isValidDate() {
		return this.mail?.date && !Number.isNaN(this.mail?.date);
	}

	/**
	 * @param {any} data
	 * @returns {Promise<any>}
	 */
	async setContent(data) {
		// // Si message texte on le met dans un textarea
		if (data.text && !data.html) {
			data.html = `<pre class="knitiv-message">${data.text}</pre>`;
			data.text = '';
		}

		if (data.html) {
			data.html = data.html

				// Traitement des pièces jointes CID pour les intégrer au mail
				.replace(/cid:([^"@]+)(@[^"@]+)?/g, (/** @type {any} */ c, /** @type {string} */ uri) => {
					if (Array.isArray(data.attachments)) {
						const attachment = data.attachments.find((/** @type {any} */ a) => a.name === uri);
						if (attachment) {
							const base64String = Buffer.from(attachment.data).toString('base64');
							return `data:${attachment.mimetype};base64,${base64String}`;
						}
					}

					return c;
				})

				// Traitement des liens (ouverture dans un nouvel onglet)
				.replace(/href=(["']?[^ "'>]+["']?)/gi, 'href=$1 target="_blank"');

			// Génération et mise en forme HTML
			const scope = this.#$rootScope.$new();
			scope.mail = data;
			scope.mail.trustAsHtml = (/** @type {string} */ html) => this.$sce.trustAsHtml(html);
			const html = await this.#$timeout(() => this.#$compile(`<div>${htmlMessage}</div>`)(scope));
			const rawHTML = this.$sce.trustAsHtml(html[0].innerHTML.replace(/<(\/)?knitiv-tag-([a-z]+)/gi, '<$1$2'));

			const base64String = Buffer.from(rawHTML, 'utf8').toString('base64');
			this.#$timeout(() => {
				data.htmlURI = this.$sce.trustAsResourceUrl(`data:text/html;base64,${base64String}`);
			});
		}
		return data;
	}

	/**
	 * @param {any} data
	 */
	async setDataFromEml(data) {
		// Fix incorrect date
		data.date = data.date instanceof Date && !Number.isNaN(data.date.valueOf()) ? data.date : null;
		this.mail = data;

		if (Array.isArray(this.mail.attachments)) {
			for (const attachment of this.mail.attachments) {
				// Si une PJ est de type text/plain, l'afficher dans le body (comportement par défaut des messageries)
				if (
					/text\/plain/i.test(attachment.contentType)
					|| (
						!this.mail.text && !this.mail.html
						&& !attachment.contentType
						&& typeof attachment.data === 'string'
						// eslint-disable-next-line no-control-regex
						&& !/[^\n\r -~]+/.test(attachment.data)
					)
				) {
					// this contains HTML code (test starting tag)
					if (/^<\/?[a-z][\S\s]*>/i.test(attachment.data)) {
						this.mail.html = attachment.data;
					}
					else {
						this.mail.text = attachment.data;
					}
					attachment.visible = true;
				}
				// Si une PJ est de type eml, l'afficher dans le body
				else if (/message\/rfc822/i.test(attachment.contentType)) {
					// @ts-ignore
					await EmlParser.read(attachment.data, async (
						/** @type {any} */ attachmentError,
						/** @type {any} */ attachmentData,
					) => {
						const attachmentScope = this.#$rootScope.$new();
						await this.setContent(attachmentData);
						attachmentScope.attachment = attachment;
						attachmentScope.data = attachmentData;
						attachmentScope.data.trustAsHtml = (/** @type {string} */ html) => this.$sce.trustAsHtml(html);

						const attachmentHTML = await this.#$timeout(
							() => this.#$compile(`<div>${attachmentMessage}</div>`)(attachmentScope),
						);
						this.mail.html += attachmentHTML[0].innerHTML;
						return this.#$timeout(() => {
							this.#$rootScope.$apply();
						});
					});
					attachment.visible = true;
				}
				else if (attachment.name && !attachment.inline) {
					attachment.visible = true;
					if (attachment.contentType) {
						const [stype, sname] = attachment.contentType.split(';');
						attachment.mimetype = stype;
						const r = /name\*?=["']?([^"]+)["']?/i.exec(sname);
						if (r && r[1]) {
							attachment.name = r[1].replace(
								/%([\da-f]{2})/gi,
								(c, p1) => String.fromCodePoint(Number.parseInt(p1, 16)),
							);
						}
					}
				}
				else {
					// Display inline images
					if (attachment.inline && /^image\//.test(attachment.contentType)) {
						const base64String = Buffer.from(attachment.data, 'utf8').toString('base64');
						if (this.mail.html) {
							this.mail.html += '<hr>';
						}
						this.mail.html += `<div style="text-align: center"><img src="data:${attachment.contentType};base64,${
							base64String}" alt="${attachment.name || ' '}"></div>`;
					}
					attachment.visible = false;
				}
			}
		}

		await this.setContent(this.mail);
	}

	async setDataFromOutlookMsg() {
		const data = this.msgReaderInstance?.getFileData();

		if (data?.dataType !== 'msg') {
			throw new Error(data?.error);
		}

		let charset;

		// get charset
		try {
			// @ts-ignore
			iconv.getCodec(data.internetCodepage);
			// @ts-ignore
			charset = iconv.encodings[data.internetCodepage];
		}
		catch {
			charset = 'utf8';
		}

		this.mail = {
			attachments: data?.attachments?.map((attachment, k) => {
				const messageAttachment = this.msgReaderInstance?.getAttachment(k);
				const messageData = messageAttachment && window.btoa(
					messageAttachment.content.reduce((p, byte) => p + String.fromCodePoint(byte), ''),
				);

				return {
					contentType: null,
					data:        messageData,
					inline:      attachment.dataType !== 'attachment',
					mimetype:    null,
					name:        attachment.name || attachment.fileName,
					visible:     attachment.dataType === 'attachment',
				};
			}),
			charset,
			date: data.clientSubmitTime ? new Date(data.clientSubmitTime) : undefined,
			from: {
				email: data.senderEmail,
				name: data.senderName,
			},
			headers: data.headers ? this.fetchRawHeaders(data.headers) : undefined,
			subject: data.subject,
			text: data.body,
			to: data.recipients?.filter(r => r.dataType === 'recipient' && r.recipType === 'to'),
		};

		await this.setContent(this.mail);
	}

	/**
	 * @param {import('angular').IOnChangesObject} changes
	 */
	$onChanges(changes) {
		if (changes.data && changes.data.previousValue !== changes.data.currentValue) {
			if (this.data) {
				if (this.type === 'message/rfc822') {
					// @ts-ignores
					EmlParser.read(new TextDecoder('utf8').decode(this.data), async (error, data) => {
						if (error) {
							console.error(error);
						}
						if (data) {
							await this.setDataFromEml(data);
						}
						this.#$timeout(() => {
							this.#$rootScope.$apply();
						});
					});
				}
				else if (this.data && this.type === 'application/vnd.ms-outlook') {
					this.msgReaderInstance = new MsgReader(this.data);
					(async () => {
						try {
							await this.setDataFromOutlookMsg();
						}
						catch (/** @type {any} */ error) {
							console.error(error);
							this.#Notification.error({
								title:   this.#$translate.instant('error.title'),
								message: error.toString(),
							});
						}
					})();
				}
			}
			else {
				this.mail = undefined;
			}
		}
	}
}
